import { NavLink, useNavigate } from "react-router-dom";
import { Home, TrendingUp, ClipboardList, User, Menu, LogOut, Ruler, CalendarClock, MessageCircle, Receipt, Sparkles, X, HeartHandshake, Ticket, RefreshCw, Gift, LifeBuoy, History, Activity, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { goToOffers } from "@/lib/offers";
import WelcomeTutorial from "@/components/WelcomeTutorial";

export default function ClientLayout({ children, fill = false }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [waitNotified, setWaitNotified] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.plan === "premium") {
      const poll = () => api.get("/coach-chat/unread").then((r) => setUnread(r.data.count)).catch(() => {});
      poll();
      const iv = setInterval(poll, 8000);
      return () => clearInterval(iv);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.plan !== "premium") {
      api.get("/waitlist/me").then((r) => setWaitNotified(r.data.status === "notified")).catch(() => {});
    }
    if (user) {
      api.get("/booking/reminder").then((r) => {
        const rem = r.data.reminder;
        if (rem && localStorage.getItem("rdv_reminder_dismissed") !== rem.booking_id) setReminder(rem);
      }).catch(() => {});
      api.get("/upsell/suggestion").then((r) => setUpsell(r.data.suggestion)).catch(() => {});
    }
  }, [user]);

  const dismissWaitlist = async () => { setWaitNotified(false); await api.post("/waitlist/dismiss").catch(() => {}); };
  const dismissReminder = () => { if (reminder) localStorage.setItem("rdv_reminder_dismissed", reminder.booking_id); setReminder(null); };
  const dismissUpsell = async () => { const kind = upsell?.type; setUpsell(null); if (kind) await api.post("/upsell/dismiss", { type: kind }).catch(() => {}); };
  const upsellCta = async () => {
    if (!upsell) return;
    if (upsell.cta_action === "navigate") { navigate(upsell.cta_target); return; }
    // Tout CTA commercial générique (renouvellement, J-7, upgrade) passe par la page de vente :
    // aucune session Stripe créée directement, aucune offre présélectionnée.
    goToOffers(navigate, { source: "banniere", returnTo: window.location.pathname, renewal: !!upsell.renewal });
  };
  const UpsellIcon = upsell?.type === "premium_upgrade" ? HeartHandshake : upsell?.type === "rdv_unique" ? Ticket : RefreshCw;

  const items = [
    { to: "/app", label: t("nav_today"), Icon: Home, testid: "nav-today" },
    { to: "/programme", label: t("nav_program"), Icon: ClipboardList, testid: "nav-program" },
    { to: "/assistant", label: t("nav_assistant"), Icon: MessageCircle, testid: "nav-assistant" },
    { to: "/evolution", label: t("nav_evolution"), Icon: TrendingUp, testid: "nav-evolution" },
    { to: "/profil", label: t("nav_profile"), Icon: User, testid: "nav-profile" },
  ];

  return (
    <div className={fill ? "App bg-sand flex flex-col overflow-hidden h-screen [height:100dvh]" : "App min-h-screen bg-sand pb-28"}>
      <WelcomeTutorial />
      <header className="shrink-0 sticky top-0 z-30 bg-sand/90 backdrop-blur-md border-b border-border">
        <div className="max-w-2xl mx-auto px-5 sm:px-6 h-16 sm:h-[68px] flex items-center justify-between">
          <img src="/logo.png" alt="MAIRUNCOACH" className="h-7 sm:h-8 w-auto" data-testid="brand-logo" />
          <div className="relative">
            <button data-testid="menu-button" onClick={() => setOpen(!open)} className="relative p-2 rounded-full hover:bg-secondary transition-[background-color]">
              <Menu size={20} strokeWidth={1.5} />
              {unread > 0 && <span data-testid="menu-unread-dot" className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-destructive ring-2 ring-white" />}
            </button>
            {open && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-border rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.06)] py-2 z-40">
                <div className="px-4 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
                <div className="border-t border-border my-1" />
                <button data-testid="menu-measurements" onClick={() => { navigate("/mensurations"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><Ruler size={18} strokeWidth={1.5}/>{t("menu_measurements")}</button>
                {user?.plan === "premium" && (
                  <button data-testid="menu-coach-chat" onClick={() => { navigate("/mon-coach"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><MessageCircle size={18} strokeWidth={1.5}/>Mon coach
                    {unread > 0 && <span data-testid="coach-unread-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[11px] font-semibold flex items-center justify-center">{unread}</span>}
                  </button>
                )}
                <button data-testid="menu-history" onClick={() => { navigate("/historique"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><History size={18} strokeWidth={1.5}/>Historique des cycles</button>
                <button data-testid="menu-complementary" onClick={() => { navigate("/sports-complementaires"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><Activity size={18} strokeWidth={1.5}/>Sports complémentaires</button>
                <button data-testid="menu-receipts" onClick={() => { navigate("/profil"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><Receipt size={18} strokeWidth={1.5}/>{t("menu_receipts")}</button>
                <button data-testid="menu-referral" onClick={() => { navigate("/parrainage"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><Gift size={18} strokeWidth={1.5}/>Parrainage</button>
                <button data-testid="menu-boutique" onClick={() => { navigate("/tarifs"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><ShoppingBag size={18} strokeWidth={1.5}/>Boutique & prestations</button>
                <button data-testid="menu-support" onClick={() => { navigate("/support"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><LifeBuoy size={18} strokeWidth={1.5}/>Support</button>
                {user?.plan === "premium"
                  ? <button data-testid="menu-booking" onClick={() => { navigate("/reservation"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><CalendarClock size={18} strokeWidth={1.5}/>{t("menu_coach_follow")}</button>
                  : <button data-testid="menu-pricing" onClick={() => { navigate("/tarifs"); setOpen(false); }} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary"><CalendarClock size={18} strokeWidth={1.5}/>{t("menu_pricing")}</button>}
                <button data-testid="menu-logout" onClick={logout} className="w-full text-left px-4 py-3 text-[15px] hover:bg-secondary flex items-center gap-3 active:bg-secondary text-destructive"><LogOut size={18} strokeWidth={1.5}/>{t("menu_logout")}</button>
                <button data-testid="menu-cgu" onClick={() => { navigate("/cgu"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-xs text-obsidian/40 hover:text-obsidian/70">Conditions Générales d'Utilisation</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {waitNotified && (
        <div data-testid="waitlist-banner" className="bg-sage text-white">
          <div className="max-w-2xl mx-auto px-5 py-2.5 flex items-center gap-3">
            <Sparkles size={16} className="shrink-0" />
            <span className="text-sm flex-1">Une place Premium s'est libérée ! Réserve la tienne avant qu'elle ne reparte.</span>
            <button data-testid="waitlist-cta" onClick={() => navigate("/tarifs")} className="rounded-full bg-white text-sage text-xs font-semibold px-3 py-1.5 whitespace-nowrap hover:bg-white/90 transition-colors">Voir l'offre</button>
            <button data-testid="waitlist-dismiss" onClick={dismissWaitlist} className="p-1 rounded-full hover:bg-white/15 transition-colors"><X size={16} /></button>
          </div>
        </div>
      )}

      {reminder && (
        <div data-testid="rdv-reminder-banner" className="bg-obsidian text-white">
          <div className="max-w-2xl mx-auto px-5 py-2.5 flex items-center gap-3">
            <CalendarClock size={16} className="shrink-0 text-sage" />
            <span className="text-sm flex-1">Rappel : ton rendez-vous avec Ton Coach est {reminder.when} à {reminder.hour}h. ({reminder.label})</span>
            <button data-testid="reminder-cta" onClick={() => navigate("/reservation")} className="rounded-full bg-white text-obsidian text-xs font-semibold px-3 py-1.5 whitespace-nowrap hover:bg-white/90 transition-colors">Voir</button>
            <button data-testid="reminder-dismiss" onClick={dismissReminder} className="p-1 rounded-full hover:bg-white/15 transition-colors"><X size={16} /></button>
          </div>
        </div>
      )}

      {upsell && (
        <div data-testid="upsell-banner" className="border-b border-border bg-gradient-to-r from-sage/10 to-transparent">
          <div className="max-w-2xl mx-auto px-5 py-3 flex items-start sm:items-center gap-3">
            <span className="shrink-0 w-8 h-8 rounded-full bg-sage/15 flex items-center justify-center"><UpsellIcon size={16} className="text-sage" /></span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-obsidian" data-testid="upsell-title">{upsell.title}</div>
              <p className="text-xs text-obsidian/60 leading-snug mt-0.5">{upsell.message}</p>
            </div>
            <button data-testid="upsell-cta" onClick={upsellCta} disabled={upsellLoading}
              className="shrink-0 rounded-full bg-obsidian text-white text-xs font-semibold px-3.5 py-2 whitespace-nowrap hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {upsellLoading ? "…" : upsell.cta_label}
            </button>
            <button data-testid="upsell-dismiss" onClick={dismissUpsell} className="shrink-0 p-1 rounded-full text-obsidian/40 hover:bg-obsidian/5 transition-colors"><X size={15} /></button>
          </div>
        </div>
      )}

      <main className={fill ? "flex-1 min-h-0 flex flex-col max-w-2xl w-full mx-auto px-5 sm:px-6" : "max-w-2xl mx-auto px-5 sm:px-6 pt-8 sm:pt-10"}>{children}</main>

      <nav className={`${fill ? "shrink-0" : "fixed bottom-0"} inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-border pb-[env(safe-area-inset-bottom)]`}>
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          {items.map(({ to, label, Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 py-3 min-h-[60px] text-[11px] tracking-wide active:scale-95 transition-[color,transform] ${isActive ? "text-obsidian" : "text-muted-foreground"}`}>
              {({ isActive }) => (<><Icon size={22} strokeWidth={isActive ? 2 : 1.5} />{label}</>)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
